import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  InputNumber,
  Badge,
  Upload,
  Divider,
  Tooltip,
  Drawer,
  Descriptions,
  Empty,
  Timeline,
  Segmented,
  Checkbox,
  Image,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  UploadOutlined,
  InboxOutlined,
  BarcodeOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  PictureOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  EyeOutlined,
  SwapOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { getStorageUrl } from '../utils/storageUtils';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

const Inventory = () => {
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();

  const [viewMode, setViewMode] = useState('table');

  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [stockFilter, setStockFilter] = useState('all');
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [stockProduct, setStockProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category_id: null,
    price: 0,
    stock_quantity: 0,
    barcode: '',
    low_stock_threshold: 10,
    description: '',
    is_active: true,
  });

  const [form] = Form.useForm();
  const [stockForm] = Form.useForm();

  // Set form values when modal opens with product data
  useEffect(() => {
    if (isProductModalOpen) {
      if (editingProduct) {
        setTimeout(() => {
          form.setFieldsValue({
            name: editingProduct.name || '',
            sku: editingProduct.sku || '',
            category_id: editingProduct.category_id,
            price: editingProduct.price || 0,
            stock_quantity: editingProduct.stock_quantity || 0,
            barcode: editingProduct.barcode || '',
            low_stock_threshold: editingProduct.low_stock_threshold || 10,
            description: editingProduct.description || '',
            is_available: editingProduct.is_available !== false,
          });
        }, 0);
      } else {
        form.resetFields();
        form.setFieldsValue({ is_available: true, stock_quantity: 0, low_stock_threshold: 10 });
      }
    }
  }, [isProductModalOpen, editingProduct, form]);

  useEffect(() => {
    const timer = setTimeout(() => {}, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const {
    data: products = [],
    isLoading,
    refetch,
    isFetching,
  } = useQuery(
    ['inventory', searchText, categoryFilter, stockFilter],
    async () => {
      try {
        const params = new URLSearchParams();
        if (searchText) params.append('search', searchText);
        if (categoryFilter) params.append('category_id', categoryFilter);
        if (stockFilter !== 'all') params.append('stock_filter', stockFilter);
        params.append('per_page', '100');

        const response = await api.get(`/admin/inventory/products?${params.toString()}`);
        return response.data?.data?.data || response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch products:', error);
        return [];
      }
    },
    { keepPreviousData: true },
  );

  const { data: categories = [] } = useQuery('inventory-categories', async () => {
    try {
      const response = await api.get('/admin/categories?type=product');
      return response.data?.data || response.data || [];
    } catch {
      return [];
    }
  });

  // Client-side filtering fallback
  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];
    let filtered = [...products];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(searchLower)) ||
          (p.sku && p.sku.toLowerCase().includes(searchLower)) ||
          (p.description && p.description.toLowerCase().includes(searchLower)),
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(
        (p) => p.category_id === categoryFilter || p.category?.id === categoryFilter,
      );
    }

    if (stockFilter !== 'all') {
      filtered = filtered.filter((p) => {
        const stock = p.stock_quantity || 0;
        const lowThreshold = p.low_stock_threshold || 10;
        if (stockFilter === 'out_of_stock') return stock === 0;
        if (stockFilter === 'low_stock') return stock > 0 && stock <= lowThreshold;
        if (stockFilter === 'in_stock') return stock > lowThreshold;
        return true;
      });
    }

    return filtered;
  }, [products, searchText, categoryFilter, stockFilter]);

  const { data: stats = {} } = useQuery('inventory-stats', async () => {
    try {
      const response = await api.get('/admin/inventory/stats');
      return response.data?.data || {};
    } catch {
      return { total_products: 0, low_stock_count: 0, out_of_stock_count: 0, total_stock_value: 0 };
    }
  });

  const { data: stockHistory = [] } = useQuery(
    ['stock-history', historyProduct?.id],
    async () => {
      if (!historyProduct?.id) return [];
      try {
        const response = await api.get(`/admin/inventory/${historyProduct.id}/history`);
        return response.data?.data || [];
      } catch {
        return [];
      }
    },
    { enabled: !!historyProduct?.id },
  );

  const updateStockMutation = useMutation(
    async ({ id, quantity, operation, reason }) => {
      const response = await api.put(`/admin/inventory/products/${id}/stock`, {
        quantity,
        operation,
        reason,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        messageApi.success('Stock updated successfully');
        setIsStockModalOpen(false);
        stockForm.resetFields();
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        queryClient.invalidateQueries({ queryKey: ['inventory-stats'] });
      },
      onError: (error) =>
        messageApi.error(error.response?.data?.message || 'Failed to update stock'),
    },
  );

  const deleteProductMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/admin/inventory/products/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        messageApi.success('Product deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        queryClient.invalidateQueries({ queryKey: ['inventory-stats'] });
      },
      onError: (error) =>
        messageApi.error(error.response?.data?.message || 'Failed to delete product'),
    },
  );

  const handleOpenProductModal = (product = null) => {
    setEditingProduct(product);
    if (product) {
      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        category_id: product.category_id || null,
        price: product.price || 0,
        stock_quantity: product.stock_quantity || 0,
        barcode: product.barcode || '',
        low_stock_threshold: product.low_stock_threshold || 10,
        description: product.description || '',
        is_active: product.is_active !== false && product.is_active !== 0,
      });
    } else {
      setFormData({
        name: '',
        sku: '',
        category_id: null,
        price: 0,
        stock_quantity: 0,
        barcode: '',
        low_stock_threshold: 10,
        description: '',
        is_active: true,
      });
    }
    setIsProductModalOpen(true);
  };

  const handleOpenStockModal = (product) => {
    setStockProduct(product);
    stockForm.setFieldsValue({ operation: 'add', quantity: 0 });
    setIsStockModalOpen(true);
  };

  const handleOpenDetailDrawer = (product) => {
    setSelectedProduct(product);
    setIsDetailDrawerOpen(true);
  };

  const handleOpenHistoryDrawer = (product) => {
    setHistoryProduct(product);
    setIsHistoryDrawerOpen(true);
  };

  const handleBarcodeSearch = async () => {
    if (!barcodeInput.trim()) return;
    try {
      const response = await api.get(`/admin/inventory/products?barcode=${barcodeInput}`);
      const result = response.data?.data?.data || response.data?.data || [];
      if (result.length === 1) {
        setSelectedProduct(result[0]);
        setIsDetailDrawerOpen(true);
        setIsBarcodeModalOpen(false);
        setBarcodeInput('');
      } else if (result.length > 1) {
        messageApi.warning(`Found ${result.length} products with this barcode`);
      } else {
        messageApi.info('No product found with this barcode');
      }
    } catch {
      messageApi.error('Failed to search by barcode');
    }
  };

  const getStockStatus = (product) => {
    const stock = product.stock_quantity || 0;
    const lowThreshold = product.low_stock_threshold || 10;
    if (stock === 0) return { color: 'red', text: 'Out of Stock' };
    if (stock <= lowThreshold) return { color: 'orange', text: 'Low Stock' };
    return { color: 'green', text: 'In Stock' };
  };

  const columns = [
    {
      title: 'Product',
      key: 'product',
      width: 280,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (_, record) => (
        <Space>
          {record.images?.[0] ? (
            <Image
              src={getStorageUrl(
                typeof record.images[0] === 'string'
                  ? record.images[0]
                  : record.images[0]?.url || record.images[0]?.image_url,
              )}
              width={40}
              height={40}
              fallback="/placeholder.png"
              preview={false}
            />
          ) : (
            <div
              style={{
                width: 40,
                height: 40,
                background: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PictureOutlined />
            </div>
          )}
          <div>
            <Text strong>{record.name}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              SKU: {record.sku || 'N/A'}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: ['category', 'name'],
      width: 120,
      sorter: (a, b) => (a.category?.name || '').localeCompare(b.category?.name || ''),
      render: (text) => <Tag>{text || 'Uncategorized'}</Tag>,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      width: 100,
      sorter: (a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0),
      render: (value) => `Rs. ${(value || 0).toLocaleString()}`,
    },
    {
      title: 'Stock',
      dataIndex: 'stock_quantity',
      width: 100,
      sorter: (a, b) => (parseInt(a.stock_quantity) || 0) - (parseInt(b.stock_quantity) || 0),
      render: (value, record) => {
        const status = getStockStatus(record);
        return (
          <Space>
            <Text strong>{value || 0}</Text>
            <Tag color={status.color}>{status.text}</Tag>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      width: 90,
      sorter: (a, b) => (a.is_active ? 1 : 0) - (b.is_active ? 1 : 0),
      render: (active) => (
        <Badge status={active ? 'success' : 'default'} text={active ? 'Active' : 'Inactive'} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleOpenDetailDrawer(record)}
            />
          </Tooltip>
          <Tooltip title="Stock">
            <Button
              type="text"
              icon={<SwapOutlined />}
              onClick={() => handleOpenStockModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleOpenProductModal(record)}
            />
          </Tooltip>
          <Tooltip title="History">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => handleOpenHistoryDrawer(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete?"
            onConfirm={() => deleteProductMutation.mutate(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const statsCards = [
    {
      title: 'Total Products',
      value: stats.total_products || 0,
      icon: <AppstoreOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
    },
    {
      title: 'Low Stock',
      value: stats.low_stock_count || 0,
      icon: <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />,
    },
    {
      title: 'Out of Stock',
      value: stats.out_of_stock_count || 0,
      icon: <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />,
    },
    {
      title: 'Stock Value',
      value: `Rs. ${(stats.total_stock_value || 0).toLocaleString()}`,
      icon: <ThunderboltOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Inventory Management
          </Title>
          <Text type="secondary">Manage your products and stock</Text>
        </div>
        <Space>
          <Button icon={<BarcodeOutlined />} onClick={() => setIsBarcodeModalOpen(true)}>
            Scan
          </Button>
          <Button icon={<UploadOutlined />} onClick={() => setIsImportModalOpen(true)}>
            Import
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenProductModal()}>
            Add Product
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statsCards.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card hoverable>
              <Statistic title={stat.title} value={stat.value} prefix={stat.icon} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Category"
              style={{ width: '100%' }}
              allowClear
              value={categoryFilter}
              onChange={setCategoryFilter}
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Stock"
              style={{ width: '100%' }}
              value={stockFilter}
              onChange={setStockFilter}
            >
              <Option value="all">All</Option>
              <Option value="in_stock">In Stock</Option>
              <Option value="low_stock">Low Stock</Option>
              <Option value="out_of_stock">Out of Stock</Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Space>
              <Segmented
                options={[
                  { value: 'table', icon: <UnorderedListOutlined /> },
                  { value: 'grid', icon: <AppstoreOutlined /> },
                ]}
                value={viewMode}
                onChange={setViewMode}
              />
              <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredProducts}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} products`,
          }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        open={isProductModalOpen}
        onCancel={() => {
          setIsProductModalOpen(false);
          setEditingProduct(null);
        }}
        footer={null}
        width={700}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '8px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Product name"
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>SKU</label>
            <Input
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              placeholder="SKU"
            />
          </div>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Category</label>
              <Select
                value={formData.category_id}
                onChange={(value) => setFormData({ ...formData, category_id: value })}
                placeholder="Select"
                style={{ width: '100%' }}
                allowClear
              >
                {categories.map((cat) => (
                  <Option key={cat.id} value={cat.id}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Price *</label>
              <InputNumber
                value={formData.price}
                onChange={(value) => setFormData({ ...formData, price: value })}
                style={{ width: '100%' }}
                min={0}
                prefix="Rs."
              />
            </Col>
          </Row>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Stock</label>
              <InputNumber
                value={formData.stock_quantity}
                onChange={(value) => setFormData({ ...formData, stock_quantity: value })}
                style={{ width: '100%' }}
                min={0}
              />
            </Col>
            <Col span={8}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Barcode</label>
              <Input
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                placeholder="Barcode"
              />
            </Col>
            <Col span={8}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                Low Stock Alert
              </label>
              <InputNumber
                value={formData.low_stock_threshold}
                onChange={(value) => setFormData({ ...formData, low_stock_threshold: value })}
                style={{ width: '100%' }}
                min={0}
              />
            </Col>
          </Row>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
              Description
            </label>
            <Input.TextArea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              placeholder="Description..."
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            >
              Active
            </Checkbox>
          </div>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              onClick={() => {
                setIsProductModalOpen(false);
                setEditingProduct(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={isSubmitting}
              onClick={async () => {
                if (!formData.name) {
                  messageApi.error('Product name is required');
                  return;
                }
                setIsSubmitting(true);
                const submitData = new FormData();
                Object.keys(formData).forEach((key) => {
                  if (formData[key] !== null && formData[key] !== undefined) {
                    // Convert boolean to 1/0 for Laravel
                    if (typeof formData[key] === 'boolean') {
                      submitData.append(key, formData[key] ? '1' : '0');
                    } else {
                      submitData.append(key, String(formData[key]));
                    }
                  }
                });
                try {
                  if (editingProduct) {
                    submitData.append('_method', 'PUT');
                    await api.post(`/admin/inventory/products/${editingProduct.id}`, submitData);
                    messageApi.success('Product updated successfully');
                  } else {
                    await api.post('/admin/inventory/products', submitData);
                    messageApi.success('Product created successfully');
                  }
                  setIsProductModalOpen(false);
                  setEditingProduct(null);
                  queryClient.invalidateQueries({ queryKey: ['inventory'] });
                  queryClient.invalidateQueries({ queryKey: ['inventory-stats'] });
                } catch (error) {
                  messageApi.error(error.response?.data?.message || 'Failed to save product');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {editingProduct ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        title="Stock Adjustment"
        open={isStockModalOpen}
        onCancel={() => {
          setIsStockModalOpen(false);
          stockForm.resetFields();
        }}
        footer={null}
      >
        {stockProduct && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <Text strong>{stockProduct.name}</Text>
            <br />
            <Text type="secondary">Current Stock: {stockProduct.stock_quantity || 0}</Text>
          </div>
        )}
        <Form
          form={stockForm}
          layout="vertical"
          onFinish={(values) => updateStockMutation.mutate({ id: stockProduct.id, ...values })}
        >
          <Form.Item name="operation" label="Operation" rules={[{ required: true }]}>
            <Select>
              <Option value="add">Add (+)</Option>
              <Option value="subtract">Remove (-)</Option>
              <Option value="set">Set (=)</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[{ required: true, type: 'number', min: 0 }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Select allowClear>
              <Option value="purchase">Purchase</Option>
              <Option value="return">Return</Option>
              <Option value="adjustment">Adjustment</Option>
              <Option value="damage">Damaged</Option>
              <Option value="sale">Sale</Option>
            </Select>
          </Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setIsStockModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={updateStockMutation.isLoading}>
              Update
            </Button>
          </Space>
        </Form>
      </Modal>

      <Drawer
        title="Product Details"
        placement="right"
        width={500}
        open={isDetailDrawerOpen}
        onClose={() => setIsDetailDrawerOpen(false)}
      >
        {selectedProduct && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                {selectedProduct.images?.[0] ? (
                  <img
                    src={getStorageUrl(
                      typeof selectedProduct.images[0] === 'string'
                        ? selectedProduct.images[0]
                        : selectedProduct.images[0]?.url || selectedProduct.images[0]?.image_url,
                    )}
                    style={{ width: '100%', borderRadius: 8 }}
                    alt="Product"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div
                    style={{
                      background: '#f0f0f0',
                      padding: 48,
                      textAlign: 'center',
                      borderRadius: 8,
                    }}
                  >
                    <PictureOutlined style={{ fontSize: 48 }} />
                    <p>No Image</p>
                  </div>
                )}
              </Col>
            </Row>
            <Descriptions column={2} bordered style={{ marginTop: 16 }}>
              <Descriptions.Item label="Name" span={2}>
                {selectedProduct.name}
              </Descriptions.Item>
              <Descriptions.Item label="SKU">{selectedProduct.sku || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Barcode">
                {selectedProduct.barcode || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {selectedProduct.category?.name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Price">
                Rs. {(selectedProduct.price || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Stock">
                {selectedProduct.stock_quantity || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  status={selectedProduct.is_active ? 'success' : 'default'}
                  text={selectedProduct.is_active ? 'Active' : 'Inactive'}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {selectedProduct.description || 'No description'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>

      <Drawer
        title={`Stock History - ${historyProduct?.name || ''}`}
        placement="right"
        width={400}
        open={isHistoryDrawerOpen}
        onClose={() => setIsHistoryDrawerOpen(false)}
      >
        <Timeline
          items={stockHistory.map((h) => ({
            color: h.operation === 'add' ? 'green' : h.operation === 'subtract' ? 'red' : 'blue',
            children: (
              <div>
                <Text strong>
                  {h.operation === 'add' ? '+' : h.operation === 'subtract' ? '-' : ''}
                  {h.quantity} units
                </Text>
                <br />
                <Text type="secondary">{h.reason || 'No reason'}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {dayjs(h.created_at).format('MMM D, YYYY h:mm A')}
                </Text>
              </div>
            ),
          }))}
        />
        {stockHistory.length === 0 && <Empty description="No history" />}
      </Drawer>

      <Modal
        title="Scan Barcode"
        open={isBarcodeModalOpen}
        onCancel={() => {
          setIsBarcodeModalOpen(false);
          setBarcodeInput('');
        }}
        footer={null}
      >
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Enter barcode"
            prefix={<BarcodeOutlined />}
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onPressEnter={handleBarcodeSearch}
          />
          <Button type="primary" onClick={handleBarcodeSearch}>
            Search
          </Button>
        </Space.Compact>
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <BarcodeOutlined style={{ fontSize: 64, color: '#1890ff' }} />
        </div>
      </Modal>

      <Modal
        title="Import Products"
        open={isImportModalOpen}
        onCancel={() => setIsImportModalOpen(false)}
        footer={null}
      >
        <Dragger
          accept=".xlsx,.xls,.csv"
          maxCount={1}
          beforeUpload={(_file) => {
            messageApi.info('Import feature coming soon');
            return false;
          }}
        >
          <p style={{ fontSize: 48 }}>
            <InboxOutlined />
          </p>
          <p>Click or drag file to upload</p>
        </Dragger>
      </Modal>
    </div>
  );
};

export default Inventory;
